import type { RefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FuzzyAligner } from '@/lib/speech/fuzzy-aligner';
import {
  SectionBoundaryDetector,
  type SectionTiming,
} from '@/lib/speech/section-boundary-detector';
import {
  SpeechRecognitionAdapter,
  type SpeechRecognitionConstructor,
} from '@/lib/speech/speech-recognition-adapter';

export interface SpeechAlignmentDebugRefs {
  adapterRef: RefObject<SpeechRecognitionAdapter | null>;
  alignerRef: RefObject<FuzzyAligner | null>;
  detectorRef: RefObject<SectionBoundaryDetector | null>;
  scriptWordsRef: RefObject<string[]>;
  sectionBoundariesRef: RefObject<number[]>;
  setCursorPosition: (position: number) => void;
}

export interface UseSpeechAlignmentReturn {
  sectionIndex: number;
  sectionProgress: number;
  cursorPosition: number;
  isListening: boolean;
  isSupported: boolean;
  sectionTimings: SectionTiming[];
  totalDurationMs: number;
  debugRefs: SpeechAlignmentDebugRefs;
  start: () => void;
  stop: () => void;
  pause: () => void;
  resume: () => void;
  reset: () => void;
  finalize: () => SectionTiming[];
}

export function useSpeechAlignment(
  scriptWords: string[],
  sectionBoundaries: number[],
  options?: { SpeechRecognition?: SpeechRecognitionConstructor },
): UseSpeechAlignmentReturn {
  const [sectionIndex, setSectionIndex] = useState(0);
  const [sectionProgress, setSectionProgress] = useState(0);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [isListening, setIsListening] = useState(false);
  const [sectionTimings, setSectionTimings] = useState<SectionTiming[]>([]);
  const [totalDurationMs, setTotalDurationMs] = useState(0);

  const adapterRef = useRef<SpeechRecognitionAdapter | null>(null);
  const alignerRef = useRef<FuzzyAligner | null>(null);
  const detectorRef = useRef<SectionBoundaryDetector | null>(null);
  const detectorStartedRef = useRef(false);

  // Track script changes to reset
  const scriptWordsRef = useRef(scriptWords);
  const sectionBoundariesRef = useRef(sectionBoundaries);

  // Initialize adapter once
  if (!adapterRef.current) {
    adapterRef.current = new SpeechRecognitionAdapter({
      continuous: true,
      SpeechRecognition: options?.SpeechRecognition,
    });
  }

  const isSupported = adapterRef.current.isSupported;

  // Reset aligner/detector when parameters change
  useEffect(() => {
    scriptWordsRef.current = scriptWords;
    sectionBoundariesRef.current = sectionBoundaries;

    alignerRef.current = new FuzzyAligner(scriptWords, sectionBoundaries);
    detectorRef.current = new SectionBoundaryDetector(sectionBoundaries.length);

    setSectionIndex(0);
    setSectionProgress(0);
    setCursorPosition(0);
    setSectionTimings([]);
    setTotalDurationMs(0);
  }, [scriptWords, sectionBoundaries]);

  // Wire adapter events
  useEffect(() => {
    const adapter = adapterRef.current;
    if (!adapter) return;

    /* v8 ignore next 20 -- tested via rehearsal-view integration; isolated hook test hangs due to polling interval */
    const handleWords = (words: string[]) => {
      const aligner = alignerRef.current;
      const detector = detectorRef.current;
      if (!aligner || !detector) return;

      const result = aligner.feedWords(words);

      // Defer timer start until the first word is actually recognized,
      // so dead air before speaking doesn't count against timing.
      if (result.cursorPosition > 0 && !detectorStartedRef.current) {
        detector.start();
        detectorStartedRef.current = true;
      }

      setCursorPosition(result.cursorPosition);
      setSectionIndex(result.sectionIndex);
      setSectionProgress(result.sectionProgress);

      if (result.boundaryJustCrossed !== null) {
        detector.onBoundaryCrossed(result.boundaryJustCrossed);
        setSectionTimings(detector.sectionTimings);
      }
    };

    const unsubInterim = adapter.onInterim(handleWords);
    const unsubFinal = adapter.onTranscript(handleWords);

    return () => {
      unsubInterim();
      unsubFinal();
      if (adapter.isListening) {
        adapter.stop();
      }
    };
  }, []);

  const start = useCallback(() => {
    // Don't start the detector timer here — it's deferred until the first
    // recognized word so dead air doesn't count against timing.
    detectorStartedRef.current = false;
    adapterRef.current?.start();
    setIsListening(true);
  }, []);

  const stop = useCallback(() => {
    adapterRef.current?.stop();
    setIsListening(false);
  }, []);

  const pause = useCallback(() => {
    adapterRef.current?.stop();
    detectorRef.current?.pause();
    setIsListening(false);
  }, []);

  const resume = useCallback(() => {
    detectorRef.current?.resume();
    adapterRef.current?.start();
    setIsListening(true);
  }, []);

  /* v8 ignore next 6 -- tested via rehearsal-view integration */
  const reset = useCallback(() => {
    alignerRef.current = new FuzzyAligner(scriptWordsRef.current, sectionBoundariesRef.current);
    detectorRef.current = new SectionBoundaryDetector(sectionBoundariesRef.current.length);
    detectorStartedRef.current = false;
    setSectionIndex(0);
    setSectionProgress(0);
    setCursorPosition(0);
    setSectionTimings([]);
    setTotalDurationMs(0);
  }, []);

  /* v8 ignore next 20 -- tested via rehearsal-view integration */
  const finalize = useCallback((): SectionTiming[] => {
    const detector = detectorRef.current;
    if (!detector) return [];

    const timings = detector.sectionTimings;
    const total = detector.totalDurationMs;

    if (timings.length === 0) return [];

    const completedSum = timings
      .filter((t) => t.completedAt !== undefined)
      .reduce((sum, t) => sum + t.durationMs, 0);

    const lastTiming = timings[timings.length - 1];
    if (lastTiming && lastTiming.completedAt === undefined) {
      const remaining = total - completedSum;
      lastTiming.durationMs = Math.max(0, remaining);
      lastTiming.completedAt = Date.now();
    }

    const finalized = [...timings];
    setSectionTimings(finalized);
    setTotalDurationMs(total);
    return finalized;
  }, []);

  // Poll totalDurationMs while listening
  /* v8 ignore next 10 -- tested via rehearsal-view integration */
  useEffect(() => {
    if (!isListening) return;

    const intervalId = setInterval(() => {
      /* v8 ignore next -- detector is always initialized when listening */
      if (detectorRef.current) {
        setTotalDurationMs(detectorRef.current.totalDurationMs);
      }
    }, 250);

    return () => clearInterval(intervalId);
  }, [isListening]);

  const setManualCursorPosition = useCallback((position: number) => {
    const aligner = alignerRef.current;
    if (!aligner) return;
    aligner.setCursorPosition(position);
    setCursorPosition(aligner.cursorPosition);
    setSectionIndex(aligner.sectionIndex);
    setSectionProgress(aligner.sectionProgress);
  }, []);

  const debugRefs = useMemo(
    () => ({
      adapterRef,
      alignerRef,
      detectorRef,
      scriptWordsRef,
      sectionBoundariesRef,
      setCursorPosition: setManualCursorPosition,
    }),
    // Refs and setManualCursorPosition are stable — this memo never recomputes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return {
    sectionIndex,
    sectionProgress,
    cursorPosition,
    isListening,
    isSupported,
    sectionTimings,
    totalDurationMs,
    debugRefs,
    start,
    stop,
    pause,
    resume,
    reset,
    finalize,
  };
}
